import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import CloseIcon from '@material-ui/icons/Close';
import FolderIcon from '@material-ui/icons/Folder';
import FolderSharedIcon from '@material-ui/icons/FolderShared';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HomeIcon from '@material-ui/icons/Home';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import styles from './DropboxFileBrowser.module.css';

const getFileIcon = (fileName, isFolder, isShared = false) => {
  if (isFolder) {
    return isShared ? <FolderSharedIcon /> : <FolderIcon />;
  }

  const extension = fileName.split('.').pop().toLowerCase();

  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extension)) {
    return <VideoLibraryIcon />;
  }
  // if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
  //   return <ImageIcon />;
  // }
  return <InsertDriveFileIcon />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${Math.round((bytes / (1024 ** i)) * 100) / 100} ${sizes[i]}`;
};

export const DropboxFileBrowser = ({ accessToken, onClose, onFilesSelected, onShowMessage, onDisconnect, onTokenExpired }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [pathHistory, setPathHistory] = useState(['']);
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingFolderId, setLoadingFolderId] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sharedFolders, setSharedFolders] = useState([]);
  const [currentNamespaceId, setCurrentNamespaceId] = useState(null);
  const [viewMode, setViewMode] = useState('my_files'); // 'my_files' or 'shared_files'
  const [showViewDropdown, setShowViewDropdown] = useState(false);

  // Pagination state
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const contentRef = React.useRef(null);

  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCursor, setSearchCursor] = useState(null);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const searchTimeoutRef = React.useRef(null);

  // Background loading state for shared folders
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [hasMoreSharedFolders, setHasMoreSharedFolders] = useState(false);
  const backgroundLoadingRef = React.useRef(false);

  const PAGE_SIZE = 25;

  // Use search results when searching, otherwise use items
  // For shared_files view at root (no namespace), use local filtering since API can't search other accounts
  const filteredItems = useMemo(() => {
    if (searchQuery.trim()) {
      // Local filter for shared_files at root level (API doesn't search other accounts' content)
      if (viewMode === 'shared_files' && !currentNamespaceId) {
        const query = searchQuery.toLowerCase().trim();
        return items.filter((item) => item.name.toLowerCase().includes(query));
      }
      return searchResults;
    }
    return items;
  }, [searchQuery, searchResults, items, viewMode, currentNamespaceId]);

  // Search function using Dropbox search_v2 API
  const performSearch = async (query, cursorParam = null) => {
    if (!query) return;

    if (!cursorParam) {
      setIsSearching(true);
      setSearchResults([]);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if in shared folder context
      if (currentNamespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: currentNamespaceId,
        });
      }

      let response;

      if (cursorParam) {
        // Continue search
        response = await axios.post(
          'https://api.dropboxapi.com/2/files/search/continue_v2',
          { cursor: cursorParam },
          { headers },
        );
      } else {
        // New search
        // For shared_files view, search without path restriction to find across all shared folders
        const searchOptions = {
          max_results: PAGE_SIZE,
          // Don't use file_categories - it doesn't support folders
          // We'll filter results manually to show folders + videos
        };

        // Only add path restriction for my_files view with a specific path
        if (viewMode === 'my_files' && currentPath) {
          searchOptions.path = currentPath;
        }

        response = await axios.post(
          'https://api.dropboxapi.com/2/files/search_v2',
          {
            query,
            options: searchOptions,
          },
          { headers },
        );
      }

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Search response:', response.data);

      // Extract matches and convert to item format
      const matches = response.data.matches || [];
      const searchItems = matches
        .map((match) => match.metadata?.metadata)
        .filter((item) => {
          if (!item) return false;
          // Filter folders and video files
          if (item['.tag'] === 'folder') return true;
          if (item['.tag'] === 'file') {
            const ext = item.name.split('.').pop().toLowerCase();
            return ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'].includes(ext);
          }
          return false;
        });

      if (cursorParam) {
        setSearchResults((prev) => [...prev, ...searchItems]);
      } else {
        setSearchResults(searchItems);
      }

      setSearchCursor(response.data.cursor);
      setHasMoreSearchResults(response.data.has_more);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Search error:', err);
    } finally {
      setIsSearching(false);
      setIsLoadingMore(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setSearchCursor(null);
      setHasMoreSearchResults(false);
      return;
    }

    // Skip API search for shared_files at root level - uses local filtering instead
    // (Dropbox search API can't search other accounts' content)
    if (viewMode === 'shared_files' && !currentNamespaceId) {
      return;
    }

    // Debounce search by 400ms
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(searchQuery.trim());
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line
  }, [searchQuery, viewMode, currentNamespaceId]);

  // Helper to map shared folder data to our format
  const mapSharedFolder = (folder) => ({
    '.tag': 'folder',
    id: folder.shared_folder_id,
    name: folder.name,
    path_display: folder.path_lower || `/${folder.name}`,
    shared_folder_id: folder.shared_folder_id,
    is_shared: true,
    preview_url: folder.preview_url,
    owner_display_name: folder.owner_display_names?.[0] || folder.owner_team?.name || 'Unknown',
  });

  // Function to fetch shared folders with pagination (25 items per page)
  const fetchSharedFolders = async (cursorParam = null) => {
    try {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetching shared folders, cursor:', cursorParam);

      let response;

      if (cursorParam) {
        // Continue from cursor
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_folders/continue',
          { cursor: cursorParam },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        // First request
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_folders',
          { limit: PAGE_SIZE },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      const folders = response.data.entries || [];
      const newCursor = response.data.cursor;

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetched:', folders.length, 'hasMore:', !!newCursor);

      return {
        folders: folders.map(mapSharedFolder),
        cursor: newCursor,
        hasMore: !!newCursor,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error fetching shared folders:', err);
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error response:', err.response?.data);
      return { folders: [], cursor: null, hasMore: false };
    }
  };

  // Function to fetch received files (files shared directly with user)
  const fetchReceivedFiles = async (cursorParam = null) => {
    try {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetching received files, cursor:', cursorParam);

      let response;

      if (cursorParam) {
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_received_files/continue',
          { cursor: cursorParam },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      } else {
        response = await axios.post(
          'https://api.dropboxapi.com/2/sharing/list_received_files',
          { limit: PAGE_SIZE },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }

      const files = response.data.entries || [];
      const newCursor = response.data.cursor;

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Fetched received files:', files.length, 'hasMore:', !!newCursor);

      // Map received files to our format
      const mappedFiles = files.map((file) => ({
        '.tag': 'file',
        id: file.id,
        name: file.name,
        path_display: file.path_display || `/${file.name}`,
        size: file.size,
        is_shared: true,
        owner_display_name: file.owner_display_names?.[0] || file.owner_team?.name || 'Unknown',
        preview_url: file.preview_url,
      }));

      return {
        files: mappedFiles,
        cursor: newCursor,
        hasMore: !!newCursor,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error fetching received files:', err);
      // Check if it's a scope permission error - gracefully handle
      if (err.response?.status === 401 || err.response?.data?.error?.['.tag'] === 'no_permission') {
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] No permission for received files, skipping');
      }
      return { files: [], cursor: null, hasMore: false };
    }
  };

  // Function to recursively get all video files from a folder
  const getAllVideosFromFolder = async (folderPath, namespaceId = null) => {
    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if we're in a shared folder context
      if (namespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: namespaceId,
        });
      }

      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: folderPath,
          recursive: true, // This will get all files from subfolders too
          include_deleted: false,
          include_mounted_folders: true,
        },
        { headers },
      );

      const allItems = response.data.entries || [];
      const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];

      // Filter to only get video files
      const videoFiles = allItems.filter((item) => {
        if (item['.tag'] === 'file') {
          const fileName = item.name.toLowerCase();
          const extension = fileName.split('.').pop();
          return videoExtensions.includes(extension);
        }
        return false;
      });

      return videoFiles.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        path: file.path_display,
      }));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading videos from folder:', err);
      return [];
    }
  };

  // Helper to filter items (folders + videos only)
  const filterItems = (allItems) => allItems.filter((item) => {
    if (item['.tag'] === 'folder') return true;
    if (item['.tag'] === 'file') {
      const fileName = item.name.toLowerCase();
      const videoExtensions = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'flv', 'wmv', 'm4v', 'mpeg', 'mpg'];
      const extension = fileName.split('.').pop();
      return videoExtensions.includes(extension);
    }
    return false;
  });

  const loadFolder = async (path, namespaceId = null) => {
    setIsLoading(true);
    setError(null);
    setCursor(null);
    setHasMore(false);

    // eslint-disable-next-line no-console
    console.log('[DROPBOX_SHARE] loadFolder called with path:', path, 'namespaceId:', namespaceId);

    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      };

      // Add namespace header if we're in a shared folder context
      if (namespaceId) {
        headers['Dropbox-API-Path-Root'] = JSON.stringify({
          '.tag': 'namespace_id',
          namespace_id: namespaceId,
        });
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Using namespace header:', headers['Dropbox-API-Path-Root']);
      }

      const response = await axios.post(
        'https://api.dropboxapi.com/2/files/list_folder',
        {
          path: path || '',
          recursive: false,
          include_deleted: false,
          include_mounted_folders: true,
          limit: PAGE_SIZE,
        },
        { headers },
      );

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] list_folder response:', response.data);

      const filteredItems = filterItems(response.data.entries || []);

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Filtered items:', filteredItems.length, 'hasMore:', response.data.has_more);

      setItems(filteredItems);
      setCursor(response.data.cursor);
      setHasMore(response.data.has_more);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading Dropbox folder:', err);
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error response:', err.response?.data);

      // Check if token expired (401 Unauthorized)
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        if (onTokenExpired) {
          onTokenExpired();
          return;
        }
      }

      setError('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  // Load more items (for infinite scroll)
  const loadMoreItems = async () => {
    if (isLoadingMore || !hasMore || !cursor) return;

    setIsLoadingMore(true);

    try {
      if (viewMode === 'shared_files') {
        // Load more shared folders
        const result = await fetchSharedFolders(cursor);
        setItems((prev) => [...prev, ...result.folders]);
        setSharedFolders((prev) => [...prev, ...result.folders]);
        setCursor(result.cursor);
        setHasMore(result.hasMore);
      } else {
        // Load more files
        const headers = {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        };

        if (currentNamespaceId) {
          headers['Dropbox-API-Path-Root'] = JSON.stringify({
            '.tag': 'namespace_id',
            namespace_id: currentNamespaceId,
          });
        }

        const response = await axios.post(
          'https://api.dropboxapi.com/2/files/list_folder/continue',
          { cursor },
          { headers },
        );

        const filteredItems = filterItems(response.data.entries || []);

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Loaded more:', filteredItems.length, 'hasMore:', response.data.has_more);

        setItems((prev) => [...prev, ...filteredItems]);
        setCursor(response.data.cursor);
        setHasMore(response.data.has_more);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading more items:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Background load remaining shared folders
  const loadRemainingSharedFolders = async (cursorToUse) => {
    if (!cursorToUse || backgroundLoadingRef.current) return;

    backgroundLoadingRef.current = true;
    setIsBackgroundLoading(true);

    let currentCursor = cursorToUse;

    try {
      while (currentCursor) {
        // Check if we should stop (e.g., view changed)
        if (!backgroundLoadingRef.current) break;

        // eslint-disable-next-line no-await-in-loop
        const result = await fetchSharedFolders(currentCursor);

        if (!backgroundLoadingRef.current) break;

        // Append new folders to both items and sharedFolders
        setItems((prev) => [...prev, ...result.folders]);
        setSharedFolders((prev) => [...prev, ...result.folders]);

        currentCursor = result.cursor;
        setHasMoreSharedFolders(!!currentCursor);

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Background loaded:', result.folders.length, 'more to load:', !!currentCursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Background loading error:', err);
    } finally {
      backgroundLoadingRef.current = false;
      setIsBackgroundLoading(false);
      setHasMoreSharedFolders(false);
    }
  };

  // Background load remaining received files
  const loadRemainingReceivedFiles = async (cursorToUse) => {
    if (!cursorToUse) return;

    let currentCursor = cursorToUse;

    try {
      while (currentCursor && backgroundLoadingRef.current !== false) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetchReceivedFiles(currentCursor);

        if (backgroundLoadingRef.current === false) break;

        // Append new files to items
        setItems((prev) => [...prev, ...result.files]);

        currentCursor = result.cursor;

        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Background loaded files:', result.files.length, 'more to load:', !!currentCursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Background loading files error:', err);
    }
  };

  // Load shared folders view (folders + received files)
  const loadSharedFoldersView = async () => {
    // Stop any ongoing background loading
    backgroundLoadingRef.current = false;

    setIsLoading(true);
    setError(null);
    setCursor(null);
    setHasMore(false);
    setHasMoreSharedFolders(false);

    try {
      // Fetch both shared folders and received files in parallel
      const [foldersResult, filesResult] = await Promise.all([
        fetchSharedFolders(),
        fetchReceivedFiles(),
      ]);

      // Combine folders and files (folders first, then files)
      const combinedItems = [...foldersResult.folders, ...filesResult.files];

      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Loaded:', foldersResult.folders.length, 'folders,', filesResult.files.length, 'files');

      setSharedFolders(foldersResult.folders);
      setItems(combinedItems);
      setHasMoreSharedFolders(foldersResult.hasMore || filesResult.hasMore);

      // Start background loading if there are more folders
      if (foldersResult.hasMore && foldersResult.cursor) {
        // eslint-disable-next-line no-console
        console.log('[DROPBOX_SHARE] Starting background loading of remaining shared folders...');
        loadRemainingSharedFolders(foldersResult.cursor);
      }

      // Background load remaining received files if any
      if (filesResult.hasMore && filesResult.cursor) {
        loadRemainingReceivedFiles(filesResult.cursor);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[DROPBOX_SHARE] Error loading shared folders view:', err);
      setError('Failed to load shared folders');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    // Stop background loading when switching views
    backgroundLoadingRef.current = false;

    setViewMode(mode);
    setShowViewDropdown(false);
    setCurrentPath('');
    setPathHistory(['']);
    setCurrentNamespaceId(null);
    setSearchQuery('');
    setCursor(null);
    setHasMore(false);
    setHasMoreSharedFolders(false);
    setIsBackgroundLoading(false);
  };

  // Scroll handler for infinite scroll
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop - clientHeight < 100 && !isLoadingMore) {
      // If searching, load more search results
      if (searchQuery.trim() && hasMoreSearchResults && searchCursor) {
        performSearch(searchQuery.trim(), searchCursor);
      } else if (!searchQuery.trim() && hasMore) {
        // Otherwise load more folder items
        loadMoreItems();
      }
    }
  };

  useEffect(() => {
    if (accessToken) {
      // If in shared_files view at root (no namespace), show shared folders list
      // Otherwise, load the folder contents (works for both my_files and inside a shared folder)
      if (viewMode === 'shared_files' && !currentNamespaceId) {
        loadSharedFoldersView();
      } else {
        loadFolder(currentPath, currentNamespaceId);
      }
    }
    // eslint-disable-next-line
  }, [currentPath, accessToken, currentNamespaceId, viewMode]);

  const handleFolderClick = (folder) => {
    // Stop background loading when navigating into any folder
    backgroundLoadingRef.current = false;
    setIsBackgroundLoading(false);

    // If folder is an object (from the items list), extract the path
    const folderPath = typeof folder === 'string' ? folder : folder.path_display;

    // eslint-disable-next-line no-console
    console.log('[DROPBOX_SHARE] handleFolderClick:', folder);

    // Check if this is a shared folder (has shared_folder_id)
    if (typeof folder === 'object' && folder.shared_folder_id) {
      // eslint-disable-next-line no-console
      console.log('[DROPBOX_SHARE] Entering shared folder with namespace:', folder.shared_folder_id);
      // Entering a shared folder - set the namespace but stay in current view mode
      setCurrentNamespaceId(folder.shared_folder_id);
      setPathHistory(['']); // Reset path history for shared folder context
      setCurrentPath(''); // Start from root of the shared folder
    } else {
      setPathHistory([...pathHistory, folderPath]);
      setCurrentPath(folderPath);
    }
    setSearchQuery(''); // Clear search when navigating
  };

  const handleBackClick = () => {
    if (pathHistory.length > 1) {
      const newHistory = [...pathHistory];
      newHistory.pop();
      const previousPath = newHistory[newHistory.length - 1];
      setPathHistory(newHistory);
      setCurrentPath(previousPath);
      setSearchQuery(''); // Clear search when navigating back
    } else if (currentNamespaceId) {
      // If we're in a shared folder namespace and at its root, go back to shared folders list
      setCurrentNamespaceId(null);
      setPathHistory(['']);
      setCurrentPath('');
      setSearchQuery('');
      // If in shared_files view, reload the shared folders list
      if (viewMode === 'shared_files') {
        loadSharedFoldersView();
      }
    }
  };

  const handleHomeClick = () => {
    setPathHistory(['']);
    setCurrentPath('');
    setCurrentNamespaceId(null); // Clear namespace when going home
    setSearchQuery(''); // Clear search when going home
  };

  const handleFileToggle = (file) => {
    const fileId = file.id;
    const isSelected = selectedFiles.some((f) => f.id === fileId);

    if (isSelected) {
      setSelectedFiles(selectedFiles.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, {
        id: fileId,
        name: file.name,
        size: file.size,
        path: file.path_display,
      }]);
    }
  };

  const handleFolderToggle = async (folder) => {
    const folderId = folder.id;
    const isSelected = selectedFolders.some((f) => f.id === folderId);

    if (isSelected) {
      // Deselect folder and remove all its files
      setSelectedFolders(selectedFolders.filter((f) => f.id !== folderId));
      const folderData = selectedFolders.find((f) => f.id === folderId);
      if (folderData && folderData.files) {
        // Remove all files that belong to this folder
        const folderFileIds = folderData.files.map((file) => file.id);
        setSelectedFiles(selectedFiles.filter((f) => !folderFileIds.includes(f.id)));
      }
    } else {
      // Select folder and get all its video files
      setLoadingFolderId(folderId);
      // Use shared_folder_id as namespace if this is a shared folder, otherwise use current namespace
      const namespaceToUse = folder.shared_folder_id || currentNamespaceId;
      // For shared folders at root level, use empty string as path; otherwise use folder path
      const pathToUse = folder.shared_folder_id ? '' : folder.path_display;
      const videoFiles = await getAllVideosFromFolder(pathToUse, namespaceToUse);
      setLoadingFolderId(null);

      if (videoFiles.length === 0) {
        // Show toast if no videos found
        if (onShowMessage) {
          onShowMessage('Nothing to select');
        }
      } else {
        // Mark each file with the folder name for category tracking
        const filesWithFolderName = videoFiles.map((file) => ({
          ...file,
          folder_name: folder.name,
        }));

        // Add folder to selected folders
        setSelectedFolders([...selectedFolders, {
          id: folderId,
          name: folder.name,
          path: folder.path_display,
          files: filesWithFolderName,
        }]);

        // Add all video files to selected files (avoid duplicates)
        const existingFileIds = selectedFiles.map((f) => f.id);
        const newFiles = filesWithFolderName.filter((file) => !existingFileIds.includes(file.id));
        setSelectedFiles([...selectedFiles, ...newFiles]);
      }
    }
  };

  const handleImport = () => {
    if (selectedFiles.length > 0) {
      onFilesSelected(selectedFiles);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);
  const isFolderSelected = (folderId) => selectedFolders.some((f) => f.id === folderId);

  const handleClearSelection = () => {
    setSelectedFiles([]);
    setSelectedFolders([]);
  };

  const handleSelectAll = () => {
    // Select all files in current folder
    const allFiles = items.filter((item) => item['.tag'] === 'file').map((file) => ({
      id: file.id,
      name: file.name,
      size: file.size,
      path: file.path_display,
    }));
    setSelectedFiles(allFiles);
  };

  const getCurrentFolderName = () => {
    // If we're in a shared folder namespace
    if (currentNamespaceId && !currentPath) {
      const sharedFolder = sharedFolders.find((f) => f.shared_folder_id === currentNamespaceId);
      return sharedFolder ? sharedFolder.name : 'Shared Folder';
    }
    if (!currentPath || currentPath === '') {
      return viewMode === 'shared_files' ? 'Shared' : 'Dropbox';
    }
    return currentPath.split('/').pop();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (showViewDropdown) {
        setShowViewDropdown(false);
      } else {
        onClose();
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showViewDropdown && !e.target.closest(`.${styles.viewModeContainer}`)) {
        setShowViewDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showViewDropdown]);

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {/* View Mode Dropdown */}
            <div className={styles.viewModeContainer}>
              <button
                className={styles.viewModeButton}
                onClick={() => setShowViewDropdown(!showViewDropdown)}
              >
                {viewMode === 'my_files' ? (
                  <>
                    <FolderIcon style={{ fontSize: 18, marginRight: 8 }} />
                    My Files
                  </>
                ) : (
                  <>
                    <FolderSharedIcon style={{ fontSize: 18, marginRight: 8 }} />
                    Shared with me
                  </>
                )}
                <ArrowDropDownIcon style={{ fontSize: 20, marginLeft: 4 }} />
              </button>
              {showViewDropdown && (
                <div className={styles.viewModeDropdown}>
                  <button
                    className={`${styles.viewModeOption} ${viewMode === 'my_files' ? styles.viewModeOptionActive : ''}`}
                    onClick={() => handleViewModeChange('my_files')}
                  >
                    <FolderIcon style={{ fontSize: 18, marginRight: 8 }} />
                    My Files
                  </button>
                  <button
                    className={`${styles.viewModeOption} ${viewMode === 'shared_files' ? styles.viewModeOptionActive : ''}`}
                    onClick={() => handleViewModeChange('shared_files')}
                  >
                    <FolderSharedIcon style={{ fontSize: 18, marginRight: 8 }} />
                    Shared with me
                  </button>
                </div>
              )}
            </div>

            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
              <button
                className={styles.breadcrumbButton}
                onClick={handleHomeClick}
                disabled={currentPath === '' && !currentNamespaceId}
              >
                <HomeIcon style={{ fontSize: 16 }} />
              </button>
              {(currentPath || currentNamespaceId) && (
                <>
                  <span className={styles.breadcrumbSeparator}>/</span>
                  <span className={styles.breadcrumbCurrent}>{getCurrentFolderName()}</span>
                </>
              )}
            </div>
          </div>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search folders and files..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className={styles.searchClearButton}
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <ClearIcon style={{ fontSize: 18 }} />
              </button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onDisconnect && (
              <Tooltip title="Switch Account" arrow>
                <button
                  className={styles.switchAccountButton}
                  onClick={onDisconnect}
                >
                  <ExitToAppIcon />
                </button>
              </Tooltip>
            )}
            <button className={styles.closeButton} onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Navigation */}
        {(pathHistory.length > 1 || currentNamespaceId) && (
          <div className={styles.navigation}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <ArrowBackIcon style={{ marginRight: 8 }} />
              Back
            </button>
            {currentNamespaceId && (
              <span className={styles.sharedIndicator}>
                (Shared Folder)
              </span>
            )}
          </div>
        )}

        {/* Content */}
        <div className={styles.content} onScroll={handleScroll} ref={contentRef}>
          {(isLoading || (isSearching && !(viewMode === 'shared_files' && !currentNamespaceId))) && (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <p>{isSearching ? 'Searching...' : 'Loading...'}</p>
            </div>
          )}

          {error && !isSearching && (
            <div className={styles.errorState}>
              <p>{error}</p>
              <button
                className={styles.retryButton}
                onClick={() => loadFolder(currentPath)}
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isBackgroundLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length === 0 && !searchQuery.trim() && (
            <div className={styles.emptyState}>
              <FolderIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>{viewMode === 'shared_files' ? 'No shared folders' : 'This folder is empty'}</p>
            </div>
          )}

          {!isLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length === 0 && searchQuery.trim() && !isBackgroundLoading && (
            <div className={styles.emptyState}>
              <SearchIcon style={{ fontSize: 64, color: '#ccc' }} />
              <p>
                No results found for &ldquo;
                {searchQuery}
                &rdquo;
              </p>
              {viewMode === 'shared_files' && !currentNamespaceId && (
                <p className={styles.searchHint}>All shared folders have been loaded</p>
              )}
            </div>
          )}

          {!isLoading && filteredItems.length === 0 && searchQuery.trim() && isBackgroundLoading && viewMode === 'shared_files' && !currentNamespaceId && (
            <div className={styles.emptyState}>
              <CircularProgress size={32} style={{ color: '#5aa5ef', marginBottom: 16 }} />
              <p>Searching through shared folders...</p>
              <p className={styles.searchHint}>More folders are still loading</p>
            </div>
          )}

          {!isLoading && (!isSearching || (viewMode === 'shared_files' && !currentNamespaceId)) && !error && filteredItems.length > 0 && (
            <div className={styles.itemList}>
              {filteredItems.map((item) => {
                const isFolder = item['.tag'] === 'folder';
                const isFile = item['.tag'] === 'file';
                const isSelected = isFile ? isFileSelected(item.id) : isFolderSelected(item.id);

                return (
                  <div
                    key={item.id}
                    className={`${styles.item} ${isFolder ? styles.itemFolder : ''} ${isSelected ? styles.itemSelected : ''} ${item.is_shared ? styles.itemShared : ''}`}
                    onClick={() => {
                      if (isFolder) {
                        handleFolderClick(item);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && isFolder) {
                        handleFolderClick(item);
                      }
                    }}
                  >
                    <div className={styles.itemIcon}>
                      {getFileIcon(item.name, isFolder, item.is_shared)}
                    </div>

                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{item.name}</div>
                      {isFile && item.size && (
                        <div className={styles.itemSize}>{formatFileSize(item.size)}</div>
                      )}
                      {item.is_shared && item.owner_display_name && (
                        <div className={styles.itemOwner}>
                          Shared by
                          {' '}
                          {item.owner_display_name}
                        </div>
                      )}
                    </div>

                    {isFile && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileToggle(item);
                        }}
                      >
                        {isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}

                    {isFolder && (
                      <button
                        className={styles.selectButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderToggle(item);
                        }}
                        disabled={loadingFolderId === item.id}
                      >
                        {loadingFolderId === item.id ? (
                          <CircularProgress size={20} style={{ color: '#667eea' }} />
                        ) : isSelected ? (
                          <CheckBoxIcon style={{ color: '#667eea' }} />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Loading more indicator */}
              {(isLoadingMore || isBackgroundLoading) && (
                <div className={styles.loadingMore}>
                  <CircularProgress size={24} style={{ color: '#5aa5ef' }} />
                  <span>
                    {isBackgroundLoading ? 'Loading all shared folders...' : 'Loading more...'}
                  </span>
                </div>
              )}

              {/* End of list indicator */}
              {!isBackgroundLoading && ((searchQuery.trim() && !hasMoreSearchResults && searchResults.length >= PAGE_SIZE)
                || (!searchQuery.trim() && !hasMore && !hasMoreSharedFolders && items.length >= PAGE_SIZE)
              ) && (
                <div className={styles.endOfList}>
                  No more items
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            {selectedFiles.length > 0 && (
              <span className={styles.selectedCount}>
                {selectedFiles.length}
                {' '}
                file
                {selectedFiles.length !== 1 ? 's' : ''}
                {' '}
                selected
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            {selectedFiles.length < items.filter((item) => item['.tag'] === 'file').length && (
              <button className={styles.cancelButton} onClick={handleSelectAll}>
                Select All
              </button>
            )}
            {selectedFiles.length > 0 && (
              <button className={styles.cancelButton} onClick={handleClearSelection}>
                Clear Selection
              </button>
            )}
            <button
              className={styles.importButton}
              onClick={handleImport}
              disabled={selectedFiles.length === 0}
            >
              Import
              {' '}
              {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
